import type { KktFilters, OfdKkt } from './types'

const KKT_STATUS_DEREGISTERED = 3

const DATE_PRESETS: Record<string, (days: number) => boolean> = {
  expired: (days) => days < 0,
  d7: (days) => days >= 0 && days <= 7,
  d30: (days) => days >= 0 && days <= 30,
  d90: (days) => days >= 0 && days <= 90,
}

export function daysLeft(dateStr?: string | null): number {
  if (!dateStr) return Number.POSITIVE_INFINITY
  const m = String(dateStr).match(/^(\d{4})-(\d{2})-(\d{2})/)
  if (m) {
    const targetUTC = Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]))
    const now = new Date()
    const todayUTC = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate())
    return Math.ceil((targetUTC - todayUTC) / (1000 * 60 * 60 * 24))
  }
  const d = new Date(dateStr)
  const now = new Date()
  d.setHours(0, 0, 0, 0)
  now.setHours(0, 0, 0, 0)
  return Math.ceil((d.getTime() - now.getTime()) / (1000 * 60 * 60 * 24))
}

export function formatDate(dateStr?: string | null): string {
  if (!dateStr) return '—'
  const m = String(dateStr).match(/^(\d{4})-(\d{2})-(\d{2})/)
  if (m) return `${m[3]}.${m[2]}.${m[1]}`
  return new Date(dateStr).toLocaleDateString('ru-RU', { timeZone: 'UTC' })
}

export function formatDateTime(ts?: string | number | null): string {
  if (ts == null) return '—'
  const d = typeof ts === 'number' ? new Date(ts * 1000) : new Date(ts)
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

/** Short type label for docs table: К. / О. / З. / … */
export function shortDocType(docType?: string, docTypeName?: string): string {
  const t = (docType || '').toLowerCase()
  if (t === 'receipt' || t === 'bso') return 'К.'
  if (t === 'receiptcorrection' || t === 'bsocorrection') return 'Кк.'
  if (t === 'openshift') return 'О.'
  if (t === 'closeshift') return 'З.'
  if (t === 'currentstate') return 'С.'
  if (docTypeName) return `${docTypeName.slice(0, 1)}.`
  return 'Д.'
}

export function isReceiptDoc(docType?: string): boolean {
  const t = (docType || '').toLowerCase()
  return t === 'receipt' || t === 'receiptcorrection' || t === 'bso' || t === 'bsocorrection'
}

export function normalizeText(v?: string | null): string {
  return String(v || '')
    .replace(/\u00A0/g, ' ')
    .replace(/\s+/g, ' ')
    .replace(/[«»“”]/g, '"')
    .replace(/[‘’]/g, "'")
    .replace(/[\u2010\u2011\u2012\u2013\u2014\u2212]/g, '-')
    .replace(/\s*№\s*/g, '№')
    .trim()
    .toLowerCase()
}

export function isKktDeregistered(kkt: OfdKkt): boolean {
  return kkt.status === KKT_STATUS_DEREGISTERED || kkt.statusText === 'Снята с регистрации'
}

export function isVisibleKkt(kkt: OfdKkt): boolean {
  return !isKktDeregistered(kkt)
}

export function isKktProblem(kkt: OfdKkt): boolean {
  if (isKktDeregistered(kkt)) return false
  const fnFinish = kkt.currentStorage?.fsFinishDate || kkt.fsFinishDate
  return (
    kkt.statusText !== 'Активирована' ||
    daysLeft(kkt.licenseFinishDate) < 0 ||
    daysLeft(fnFinish) < 0
  )
}

export function getDateClass(dateStr?: string | null): 'ok' | 'warning' | 'expired' {
  const days = daysLeft(dateStr)
  if (days < 0) return 'expired'
  if (days <= 30) return 'warning'
  return 'ok'
}

export function dateToTimestamp(dateStr?: string | null): number | null {
  if (!dateStr) return null
  const m = String(dateStr).match(/^(\d{4})-(\d{2})-(\d{2})/)
  if (m) return Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]))
  const d = new Date(dateStr)
  return Number.isNaN(d.getTime()) ? null : d.getTime()
}

export function applyKktFilters(kkts: OfdKkt[], filters: KktFilters): OfdKkt[] {
  let out = kkts.filter(isVisibleKkt)
  if (filters.problemOnly) out = out.filter(isKktProblem)
  if (filters.status) out = out.filter((x) => x.statusText === filters.status)
  if (filters.model) out = out.filter((x) => x.model === filters.model)
  if (filters.salesPoint) {
    out = out.filter((x) => normalizeText(x.kktSalesPoint) === normalizeText(filters.salesPoint))
  }
  if (filters.search) {
    const s = filters.search.toLowerCase()
    out = out.filter((x) =>
      [x.regId, x.factoryNumber, x.factoryId, x.fsNumber, x.model, x.organizationName, x.kktSalesPoint]
        .join(' ')
        .toLowerCase()
        .includes(s),
    )
  }
  if (filters.fnPreset && DATE_PRESETS[filters.fnPreset]) {
    out = out.filter((x) =>
      DATE_PRESETS[filters.fnPreset](daysLeft(x.currentStorage?.fsFinishDate || x.fsFinishDate)),
    )
  }
  if (filters.licensePreset && DATE_PRESETS[filters.licensePreset]) {
    out = out.filter((x) => DATE_PRESETS[filters.licensePreset](daysLeft(x.licenseFinishDate)))
  }

  if (filters.dateSort) {
    const compare = (aMs: number | null, bMs: number | null, dir: 'asc' | 'desc') => {
      if (aMs == null && bMs == null) return 0
      if (aMs == null) return 1
      if (bMs == null) return -1
      return dir === 'asc' ? aMs - bMs : bMs - aMs
    }
    out = [...out].sort((a, b) => {
      if (filters.dateSort === 'fn-asc' || filters.dateSort === 'fn-desc') {
        const res = compare(
          dateToTimestamp(a.currentStorage?.fsFinishDate || a.fsFinishDate),
          dateToTimestamp(b.currentStorage?.fsFinishDate || b.fsFinishDate),
          filters.dateSort === 'fn-asc' ? 'asc' : 'desc',
        )
        if (res !== 0) return res
      }
      if (filters.dateSort === 'lic-asc' || filters.dateSort === 'lic-desc') {
        const res = compare(
          dateToTimestamp(a.licenseFinishDate),
          dateToTimestamp(b.licenseFinishDate),
          filters.dateSort === 'lic-asc' ? 'asc' : 'desc',
        )
        if (res !== 0) return res
      }
      return String(a.regId || '').localeCompare(String(b.regId || ''))
    })
  }

  return out
}

export function computeKpi(kkts: OfdKkt[]) {
  const all = kkts.filter(isVisibleKkt)
  return {
    total: all.length,
    expiringFn: all.filter((x) => {
      const d = daysLeft(x.currentStorage?.fsFinishDate || x.fsFinishDate)
      return d >= 0 && d <= 30
    }).length,
    expiringLicense: all.filter((x) => {
      const d = daysLeft(x.licenseFinishDate)
      return d >= 0 && d <= 30
    }).length,
    expiredFn: all.filter((x) => daysLeft(x.currentStorage?.fsFinishDate || x.fsFinishDate) < 0).length,
    expiredLicense: all.filter((x) => daysLeft(x.licenseFinishDate) < 0).length,
    problem: all.filter(isKktProblem).length,
    active: all.filter((x) => x.statusText === 'Активирована').length,
  }
}

export function todayYmd(): string {
  return new Date().toLocaleDateString('sv-SE')
}

export function ymdFromLocalDate(d: Date): string {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

export function fnPlanPresetNextMonth(): { from: string; to: string } {
  const now = new Date()
  const from = new Date(now.getFullYear(), now.getMonth() + 1, 1)
  const to = new Date(now.getFullYear(), now.getMonth() + 2, 0)
  return { from: ymdFromLocalDate(from), to: ymdFromLocalDate(to) }
}

export function getKktRowClass(
  kkt: OfdKkt,
  selectedRegId?: string,
  selectedInn?: string,
): string {
  const classes: string[] = []
  if (kkt.statusText !== 'Активирована') classes.push('problem')
  else {
    const fnFinish = kkt.currentStorage?.fsFinishDate || kkt.fsFinishDate
    if (daysLeft(fnFinish) < 0) classes.push('expired-fn')
    else if (daysLeft(fnFinish) <= 30) classes.push('warning-fn')
  }
  if (selectedRegId && selectedRegId === kkt.regId && selectedInn === kkt.inn) {
    classes.push('selected')
  }
  return classes.join(' ')
}

const KKT_IMAGES: Record<string, string> = {
  'АТОЛ 42ФА': 'https://atol.ru/upload/iblock/c1f/atol-42fa.png',
  'АТОЛ 42ФС': 'https://atol.ru/upload/iblock/c1f/atol-42fa.png',
  'АТОЛ 47ФА': 'https://atol.ru/upload/iblock/c1f/atol-42fa.png',
  'АТОЛ 55 v2 Ф': 'https://atol.ru/upload/iblock/c1f/atol-42fa.png',
  'АТОЛ 55Ф': 'https://atol.ru/upload/iblock/c1f/atol-42fa.png',
  'АТОЛ 77Ф': 'https://atol.ru/upload/iblock/c1f/atol-42fa.png',
  'АТОЛ 91Ф': 'https://atol.ru/upload/iblock/c1f/atol-42fa.png',
  'АТОЛ FPrint-22ПТК':
    'https://www.xn--b1aaeeabb0hpc6ae.xn--p1ai/assets/images/products/6416/big/fiskalnyj-registrator-atol-fprint-22ptk-chernyj-bez-fnbez-envd-rsusbethernet.jpg',
  'СП802-Ф':
    'https://www.digitalserv.ru/media_files/2020/02/ds_ds_d221d2327c6a1a17b25275161ba30cd1d32097a3dd3fee5d6e13e7dc0f4cc.jpg',
  'СПАРК-115-Ф': 'https://atol.ru/upload/iblock/c1f/atol-42fa.png',
  'ЭВОТОР СТ2Ф': 'https://atol.ru/upload/iblock/c1f/atol-42fa.png',
}

export function getKktImageUrl(model?: string | null): string | null {
  if (!model) return null
  const raw = model.trim()
  if (KKT_IMAGES[raw]) return KKT_IMAGES[raw]
  for (const [key, url] of Object.entries(KKT_IMAGES)) {
    if (raw.includes(key) || key.includes(raw)) return url
  }
  return null
}
